import { useCallback, useEffect, useState } from 'react';
import type { PublicStream } from '@zip2live/shared';
import { broadcasterLabel } from '@zip2live/shared/stream-state';
import { Footer, Header, LiveBadge } from '../components/Brand.tsx';
import { StreamStats } from '../components/StreamStats.tsx';
import { ListedSwitch } from '../components/ListedSwitch.tsx';
import { useMe } from '../lib/me.ts';
import { ShareBar } from '../components/ShareBar.tsx';
import { TitleEditor } from '../components/TitleEditor.tsx';
import { api } from '../lib/api.ts';
import { ensureSession } from '../lib/auth.ts';
import { ensureAgreed } from '../lib/rules.ts';
import { copyText } from '../lib/share.ts';
import { NotifyFollowersSwitch } from '../components/FollowButton.tsx';

interface ObsState {
  server: string;
  streamKey: string | null;
  current: PublicStream | null;
  /** Measured keyframe interval when it is too long for low-delay streaming. */
  keyframeSeconds: number | null;
  /** Transparent chat overlay for an OBS Browser Source; follows every broadcast. */
  chatOverlayUrl: string;
  /** "Notify followers" for streams OBS starts (and the current one, until it's live). */
  notifyFollowers: boolean;
  followers: number;
  /** Title the next stream starts with (OBS opens it by itself on Start Streaming). */
  nextTitle: string | null;
}

/**
 * OBS setup. The key is permanent for this creator: paste it into OBS once, and every
 * Start Streaming opens (or resumes) a broadcast. A link can be grabbed before going live.
 */
export function Obs() {
  const [state, setState] = useState<ObsState | null>(null);
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setState(await api.obs());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void ensureSession().then(refresh, (e: Error) => setError(e.message));
    // Picks up the stream OBS creates when you press Start Streaming. Cheap and only while this page is open.
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const cur = state?.current;
  const canList = Boolean(useMe().me?.user?.isVerified);
  const open = cur && !['ENDED', 'EXPIRED', 'FAILED'].includes(cur.status);

  const waitingForObs = open && (cur.status === 'READY' || cur.status === 'CREATED');

  return (
    <main className="min-h-full bg-paper">
      <Header tone="paper" />
      <div className="mx-auto flex max-w-6xl flex-wrap items-start gap-8 px-5 pt-8 pb-20 sm:px-8 sm:pt-12">
        <div className="min-w-0 flex-[999_1_560px]">
          <h1 className="speed-text text-[clamp(2.5rem,7vw,3.5rem)] leading-[0.95]">
            Stream from <span className="text-race">OBS</span>
          </h1>
          <p className="mt-3 max-w-xl text-lg text-muted">Set it up once. After that, pressing Start Streaming in OBS opens a broadcast and your link plays it.</p>
          {!state && !error && <p className="mt-8 font-semibold">Loading…</p>}

          {state && (
            <ol className="mt-8 flex flex-col gap-4">
              <Step n={1}>
                In OBS open <b>Settings → Stream</b> and pick Service <b>Custom</b>.
              </Step>
              <Step n={2}>
                Paste your server and stream key.
                {state.streamKey ? (
                  <div className="mt-5 space-y-4 font-normal">
                    <Field label="Server" value={state.server} />
                    <Field label="Stream key" value={state.streamKey} secret={!reveal} onReveal={() => setReveal(true)} />
                    <p className="text-sm text-muted">
                      Keep your stream key private: anyone with it can broadcast as you.{' '}
                      <button type="button" className="font-bold text-navy underline underline-offset-2" onClick={() => confirm('Make a new key? OBS will need the new one.') && void run(async () => (await ensureAgreed('live')) && api.obsKey(true))}>
                        Reset key
                      </button>
                    </p>
                  </div>
                ) : (
                  <div className="mt-5">
                    <button type="button" onClick={() => void run(async () => (await ensureAgreed('live')) && api.obsKey())} className="btn-race shadow-race-navy h-14 px-7 text-xl">
                      Get server and key
                    </button>
                  </div>
                )}
              </Step>
              <Step n={3}>
                Press <b>Start Streaming</b>. That’s it, every time.
              </Step>
            </ol>
          )}
          {error && <p role="alert" className="mt-5 rounded-xl bg-race/10 px-4 py-3 font-semibold text-race-600">{error}</p>}
        </div>

        {state?.streamKey && (
          <aside className="flex min-w-0 flex-[1_1_340px] flex-col gap-4 lg:mt-[7.5rem]">
            <div className="rounded-3xl bg-navy p-6 text-white">
              {open ? (
                <>
                  <div className="flex items-center gap-2.5">
                    {cur.status === 'LIVE' ? <LiveBadge /> : <span className={`h-3 w-3 rounded-full ${waitingForObs ? 'bg-[#ffb020]' : 'bg-race-300'}`} aria-hidden />}
                    <span className="font-extrabold">{cur.status === 'LIVE' ? 'OBS is connected' : broadcasterLabel(cur.status)}{waitingForObs ? ' · waiting for OBS' : ''}</span>
                  </div>
                  <TitleEditor
                    tone="dark"
                    title={cur.title}
                    onSave={async (title) => {
                      await api.setTitle(cur.id, title);
                      await refresh();
                    }}
                    className="mt-3 -ml-0.5"
                  />
                  <p className="mt-2 mb-4 text-[15px] text-muted-dark">
                    {cur.status === 'LIVE' ? 'Viewers are watching at this link.' : 'Your viewer link is ready. Share it now and it plays the moment OBS connects.'}
                  </p>
                  {cur.status === 'LIVE' && <StreamStats stream={cur} className="-mt-1.5 mb-4" />}
                  <div className="mb-4">
                    <ListedSwitch listed={cur.listed} canList={canList} onChange={(next) => void run(() => api.setListed(cur.id, next))} tone="dark" />
                  </div>
                  <ShareBar url={cur.watchUrl} dark compact />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2.5">
                    <span className="h-3 w-3 rounded-full bg-faint-dark" aria-hidden />
                    <span className="font-extrabold">Not live</span>
                  </div>
                  <p className="mt-2.5 text-[15px] text-muted-dark">Press Start Streaming in OBS, or grab a link to share before you start.</p>
                  <p className="mt-4 text-xs font-extrabold tracking-[0.08em] text-faint-dark uppercase">Title for your next stream</p>
                  <TitleEditor
                    tone="dark"
                    title={state.nextTitle}
                    label="Title for your next stream"
                    onSave={async (title) => {
                      await api.setNextTitle(title);
                      await refresh();
                    }}
                    className="mt-1 -ml-0.5"
                  />
                  <button type="button" className="mt-4 h-12 w-full rounded-xl bg-white font-extrabold text-navy hover:bg-mist" onClick={() => void run(() => api.createStream({ source: 'obs', notifyFollowers: state.notifyFollowers }, crypto.randomUUID()))}>
                    Get a link for my next stream
                  </button>
                </>
              )}
            </div>
            {state.followers > 0 && (
              <NotifyFollowersSwitch
                tone="light"
                on={state.notifyFollowers}
                followers={state.followers}
                onChange={(on) => {
                  setState({ ...state, notifyFollowers: on });
                  void run(() => api.setObsNotifyFollowers(on));
                }}
              />
            )}
            {open && state.keyframeSeconds && (
              <p role="status" className="rounded-2xl bg-race/10 px-4 py-3 text-sm font-semibold text-race-600">
                Your keyframes are about {state.keyframeSeconds} s apart, which adds delay for viewers. In OBS go to Settings → Output → Streaming and set Keyframe Interval to 1 s.
              </p>
            )}
            <div className="rounded-3xl border border-line bg-white p-5 sm:p-6">
              <p className="font-extrabold">Live chat</p>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                Read and moderate chat in its own window while you stream. To show chat on screen, add the overlay link in OBS as a <b className="text-navy">Browser Source</b> (once: it follows every broadcast).
              </p>
              {open && (
                <button type="button" className="mt-4 h-12 w-full rounded-xl bg-navy font-extrabold text-white hover:bg-navy-700" onClick={() => window.open(`/s/${cur.id}/chat`, 'zip2live-chat', 'popup,width=420,height=760')}>
                  Open chat window
                </button>
              )}
              <div className="mt-4">
                <Field label="Chat overlay for OBS" value={state.chatOverlayUrl} />
              </div>
            </div>
            <div className="rounded-3xl border border-line bg-white p-5 sm:p-6">
              <p className="font-extrabold">For the lowest delay</p>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                Settings → Output → Streaming: <b className="text-navy">Keyframe Interval 1 s</b> (not 0/auto, which can mean 8 s), H.264, AAC audio, 4,500–6,000 Kbps at 1080p30 or 720p60. Hardware encoders (Apple VT, NVENC) add the least delay. At 720p, viewers on slow connections also get a lighter 360p copy.
              </p>
            </div>
          </aside>
        )}
      </div>
      <Footer />
    </main>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-4 rounded-3xl border border-line bg-white p-5 sm:p-6">
      <span className="speed-text w-6 shrink-0 text-3xl leading-none text-race" aria-hidden>{n}</span>
      <div className="min-w-0 flex-1 pt-0.5 text-[17px] font-bold">{children}</div>
    </li>
  );
}

function Field({ label, value, secret, onReveal }: { label: string; value: string; secret?: boolean; onReveal?: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <label className="block">
      <span className="text-xs font-extrabold tracking-[0.08em] text-muted uppercase">{label}</span>
      <div className="mt-1.5 flex gap-2">
        <input readOnly type={secret ? 'password' : 'text'} value={value} className="h-12 min-w-0 flex-1 rounded-xl border-2 border-line bg-white px-3.5 font-mono text-sm text-navy outline-none focus:border-navy" />
        {secret && <button type="button" onClick={onReveal} className="rounded-xl border-2 border-line px-4 text-sm font-extrabold hover:bg-mist">Show</button>}
        <button type="button" onClick={async () => setCopied((await copyText(value)) === 'copied')} className="rounded-xl bg-navy px-4 text-sm font-extrabold text-white hover:bg-navy-700">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </label>
  );
}
